import { useState, useCallback } from 'react';
import {
  makeStyles,
  Body2,
  Caption1,
  Badge,
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
  Input,
  Dropdown,
  Option,
  Field,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { PageTitle } from '@/components/PageTitle';
import { Add24Regular, ArrowSync24Regular } from '@fluentui/react-icons';
import { DataTable, type Column } from '@/components/DataTable';
import { useSharePointList } from '@/hooks/useSharePointList';
import type {
  Application,
  AppCategory,
  LicenseModel,
  BillingCycle,
  SPListItem,
} from '@/models/types';
import { formatCurrency } from '@/utils/formatters';
import { APP_CATEGORIES } from '@/utils/constants';
import { useAppContext } from '@/store/appContext';
import { canManageAppsDelegated, canSyncData } from '@/utils/permissions';
import type { AppAdmin } from '@/models/types';
import {
  fetchSubscribedSkus,
  syncLicensesToSharePoint,
  type LicenseSyncResult,
} from '@/graph/licenseService';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '20px' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '12px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    '@media (max-width: 480px)': {
      gridTemplateColumns: '1fr',
    },
  },
});

export function Applications() {
  const styles = useStyles();
  const { graphClient, currentUser, listIds } = useAppContext();
  // Delegated: admin + any emails in OC_AppAdmins list can manage
  const { items: appAdmins } = useSharePointList<AppAdmin>('OC_AppAdmins', {
    autoFetch: !!listIds,
  });
  const delegatedAdminEmails = appAdmins.map((a) => a.fields.Email).filter(Boolean);
  const canEdit = currentUser
    ? canManageAppsDelegated(currentUser.role, currentUser.email, delegatedAdminEmails)
    : false;
  const canSync = currentUser ? canSyncData(currentUser.role) : false;

  const { items: apps, loading, create, update, refresh } =
    useSharePointList<Application>('OC_Applications', {
      autoFetch: !!listIds,
    });

  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<SPListItem<Application> | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<LicenseSyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    Title: '',
    Vendor: '',
    Category: 'DevTools' as AppCategory,
    LicenseModel: 'PerUser' as LicenseModel,
    MonthlyCostPerLicense: 0,
    BillingCycle: 'Monthly' as BillingCycle,
    MicrosoftSKU: '',
    IsAutoSynced: false,
  });

  const openNewForm = () => {
    setFormData({
      Title: '',
      Vendor: '',
      Category: 'DevTools',
      LicenseModel: 'PerUser',
      MonthlyCostPerLicense: 0,
      BillingCycle: 'Monthly',
      MicrosoftSKU: '',
      IsAutoSynced: false,
    });
    setEditItem(null);
    setShowForm(true);
  };

  const openEditForm = (item: SPListItem<Application>) => {
    setFormData({ ...item.fields });
    setEditItem(item);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.Title) {
      setError('Title is required');
      return;
    }
    if (editItem) {
      await update(editItem.id, formData);
    } else {
      await create(formData);
    }
    setShowForm(false);
    setError(null);
  };

  const handleSyncLicenses = useCallback(async () => {
    if (!graphClient) return;
    setSyncing(true);
    setSyncResult(null);
    setError(null);

    try {
      const skus = await fetchSubscribedSkus(graphClient);
      const result = await syncLicensesToSharePoint(graphClient, skus);
      setSyncResult(result);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'License sync failed');
    } finally {
      setSyncing(false);
    }
  }, [graphClient, refresh]);

  const filteredApps =
    categoryFilter === 'all'
      ? apps
      : apps.filter((a) => a.fields.Category === categoryFilter);

  const columns: Column<SPListItem<Application>>[] = [
    {
      key: 'title',
      label: 'Application',
      sortable: true,
      getValue: (item) => item.fields.Title,
      render: (item) => (
        <div>
          <Body2 style={{ fontWeight: 500 }}>{item.fields.Title}</Body2>
          <Caption1>{item.fields.Vendor}</Caption1>
        </div>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      render: (item) => (
        <Badge appearance="outline" size="small">
          {item.fields.Category}
        </Badge>
      ),
      getValue: (item) => item.fields.Category,
      sortable: true,
    },
    {
      key: 'licenseModel',
      label: 'License',
      getValue: (item) => item.fields.LicenseModel,
    },
    {
      key: 'cost',
      label: 'Cost/License',
      render: (item) => (
        <Caption1>
          {item.fields.MonthlyCostPerLicense > 0
            ? `${formatCurrency(item.fields.MonthlyCostPerLicense)}/${item.fields.BillingCycle === 'Monthly' ? 'mo' : 'yr'}`
            : 'Not set'}
        </Caption1>
      ),
      getValue: (item) => item.fields.MonthlyCostPerLicense,
      sortable: true,
    },
    {
      key: 'source',
      label: 'Source',
      render: (item) =>
        item.fields.IsAutoSynced ? (
          <Badge appearance="filled" color="brand" size="small">
            Microsoft
          </Badge>
        ) : (
          <Badge appearance="tint" size="small">
            Manual
          </Badge>
        ),
    },
    ...(canEdit
      ? [
          {
            key: 'actions',
            label: '',
            width: '60px',
            render: (item: SPListItem<Application>) => (
              <Button
                appearance="subtle"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openEditForm(item);
                }}
              >
                Edit
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <PageTitle>Applications</PageTitle>
      </div>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {syncResult && (
        <MessageBar intent="success">
          <MessageBarBody>
            License sync complete: {syncResult.skusProcessed} SKUs processed,{' '}
            {syncResult.appsCreated} new apps, {syncResult.appsUpdated} updated.
          </MessageBarBody>
        </MessageBar>
      )}

      <DataTable
        columns={columns}
        items={filteredApps}
        loading={loading}
        searchPlaceholder="Search applications..."
        searchFields={(item) =>
          `${item.fields.Title} ${item.fields.Vendor} ${item.fields.Category}`
        }
        getRowKey={(item) => item.id}
        emptyMessage="No applications registered. Add one or sync Microsoft licenses."
        renderMobile={(item) => ({
          title: item.fields.Title,
          subtitle: item.fields.Vendor,
          meta: `${item.fields.Category} · ${
            item.fields.MonthlyCostPerLicense > 0
              ? `${formatCurrency(item.fields.MonthlyCostPerLicense)}/${item.fields.BillingCycle === 'Monthly' ? 'mo' : 'yr'}`
              : 'no cost set'
          }`,
          trailing: item.fields.IsAutoSynced ? (
            <Badge appearance="filled" color="brand" size="small">
              MS
            </Badge>
          ) : (
            <Badge appearance="tint" size="small">
              Manual
            </Badge>
          ),
        })}
        filterTabs={[
          { key: 'all', label: 'All', count: apps.length },
          ...APP_CATEGORIES.map((c) => ({
            key: c,
            label: c,
            count: apps.filter((a) => a.fields.Category === c).length,
          })),
        ]}
        activeFilter={categoryFilter}
        onFilterChange={setCategoryFilter}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            {canSync && (
              <Button
                appearance="outline"
                icon={
                  syncing ? <Spinner size="tiny" /> : <ArrowSync24Regular />
                }
                disabled={syncing}
                onClick={handleSyncLicenses}
              >
                {syncing ? 'Syncing...' : 'Sync Microsoft Licenses'}
              </Button>
            )}
            {canEdit && (
              <Button
                appearance="primary"
                icon={<Add24Regular />}
                onClick={openNewForm}
              >
                Add Application
              </Button>
            )}
          </div>
        }
      />

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={() => setShowForm(false)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {editItem ? 'Edit Application' : 'Add Application'}
            </DialogTitle>
            <DialogContent>
              <div className={styles.form}>
                <div className={styles.row}>
                  <Field label="Name" required>
                    <Input
                      value={formData.Title}
                      onChange={(_, d) =>
                        setFormData((f) => ({ ...f, Title: d.value }))
                      }
                    />
                  </Field>
                  <Field label="Vendor">
                    <Input
                      value={formData.Vendor}
                      onChange={(_, d) =>
                        setFormData((f) => ({ ...f, Vendor: d.value }))
                      }
                    />
                  </Field>
                </div>
                <div className={styles.row}>
                  <Field label="Category">
                    <Dropdown
                      value={formData.Category}
                      onOptionSelect={(_, d) =>
                        setFormData((f) => ({
                          ...f,
                          Category: d.optionValue as AppCategory,
                        }))
                      }
                    >
                      {APP_CATEGORIES.map((c) => (
                        <Option key={c} value={c}>
                          {c}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                  <Field label="License Model">
                    <Dropdown
                      value={formData.LicenseModel}
                      onOptionSelect={(_, d) =>
                        setFormData((f) => ({
                          ...f,
                          LicenseModel: d.optionValue as LicenseModel,
                        }))
                      }
                    >
                      <Option value="PerUser">Per User</Option>
                      <Option value="PerOrg">Per Org</Option>
                      <Option value="Subscription">Subscription</Option>
                      <Option value="Free">Free</Option>
                    </Dropdown>
                  </Field>
                </div>
                <div className={styles.row}>
                  <Field label="Cost per License (GBP)">
                    <Input
                      type="number"
                      value={String(formData.MonthlyCostPerLicense)}
                      onChange={(_, d) =>
                        setFormData((f) => ({
                          ...f,
                          MonthlyCostPerLicense: Number(d.value) || 0,
                        }))
                      }
                    />
                  </Field>
                  <Field label="Billing Cycle">
                    <Dropdown
                      value={formData.BillingCycle}
                      onOptionSelect={(_, d) =>
                        setFormData((f) => ({
                          ...f,
                          BillingCycle: d.optionValue as BillingCycle,
                        }))
                      }
                    >
                      <Option value="Monthly">Monthly</Option>
                      <Option value="Annually">Annually</Option>
                    </Dropdown>
                  </Field>
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
              <Button appearance="primary" onClick={handleSave}>
                {editItem ? 'Save' : 'Add Application'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
